import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import {
  Award,
  Shield,
  Heart,
  Users,
  GraduationCap,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  BookOpen,
  Building2,
  Target,
  Sparkles,
  Phone,
  Mail,
  Loader2,
  Edit2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for system checklist
const CHECKLIST_ICONS = [BookOpen, Target, Building2, Shield];
// Icon mapping for authority badges
const BADGE_ICONS = [Award, Shield, GraduationCap, Heart];

const FounderPage = () => {
  const contactRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const { isAuthenticated, isAdmin } = useAuth();
  
  // Check if current user is admin
  const isAdminUser = isAuthenticated && isAdmin && isAdmin();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/founder-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load founder config:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Fallback defaults
  const c = config || {};
  const name = c.name || 'Sam Oyelakun';
  const role = c.role || 'Founder & Head Coach';
  const org = c.org || 'Regional Coordinator – Eswatini Drowning Prevention Alliance';
  const headline = c.headline || 'Meet the Founder';
  const quote = c.quote || 'Water safety is not a luxury — it is a life skill.';
  const phone = c.phone || '+268 7600 1234';
  const email = c.email || 'info@eswatiniseals.com';
  const portraitUrl = getImageUrl(c.portrait_url);
  const actionImageUrl = getImageUrl(c.action_image_url);
  const ctaPrimaryText = c.cta_primary_text || 'View Programs';
  const ctaPrimaryLink = c.cta_primary_link || '/#programs';
  const ctaSecondaryText = c.cta_secondary_text || 'Partner With Us';
  const visionTitle = c.vision_title || 'Building Water-Confident Generations';
  const visionText = c.vision_text || 'We are building a generation that sees water not as a threat, but as a skill mastered with confidence and discipline. Every child, every adult, deserves the opportunity to feel safe in and around water.';
  const systemTitle = c.system_title || 'A Structured Approach to Safety';
  const systemChecklist = c.system_checklist?.length > 0 ? c.system_checklist : [
    'Structured curriculum with progressive skill levels',
    'Digital progress tracking for every learner',
    'School partnerships for community-wide impact',
    'National drowning prevention advocacy',
  ];
  const authorityBadges = c.authority_badges?.length > 0 ? c.authority_badges : [
    { label: 'Certified Instructor' },
    { label: 'Water Safety Advocate' },
    { label: 'School Program Director' },
    { label: 'Community Leader' },
  ];
  const actionHeadline = c.action_headline || 'Where Leadership Meets Action';
  const actionText = c.action_text || 'Sam actively trains learners in the water, guiding them step-by-step toward confidence, skill mastery, and safety.';
  const ctaSectionHeadline = c.cta_section_headline || 'Building Water-Safe Communities Across Eswatini';
  const ctaSectionText = c.cta_section_text || "Whether you're enrolling a child, partnering as a school, or supporting our mission — every action brings us closer to a drowning-free future.";
  const ctaEnrollText = c.cta_enroll_text || 'Enroll a Learner';
  const ctaPartnerText = c.cta_partner_text || 'Partner With Us';

  return (
    <div className="min-h-screen bg-white" data-testid="founder-page">
      {/* Admin Edit Button - Fixed Position */}
      {isAdminUser && (
        <Link
          to="/admin/founder"
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-lg hover:shadow-xl hover:border-primary transition-all text-sm font-medium text-slate-700 hover:text-primary"
          data-testid="edit-founder-page-btn"
        >
          <Edit2 className="w-4 h-4" />
          Edit Founder
        </Link>
      )}
      
      {/* ========== SECTION 1: HERO - AUTHORITY LAYER ========== */}
      <section className="relative pt-20 pb-16 lg:pb-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Back to Home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary mb-8 transition-colors"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            Back to Home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* LEFT: Portrait */}
            <div className="order-1 lg:order-1">
              <div className="relative max-w-md mx-auto lg:mx-0">
                {/* Portrait Frame - 4:5 Aspect Ratio */}
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl border-4 border-slate-200">
                  {portraitUrl ? (
                    <img
                      src={portraitUrl}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center">
                      <div className="text-center p-8">
                        <div className="w-28 h-28 mx-auto mb-6 rounded-full bg-slate-600/50 flex items-center justify-center border-2 border-slate-500/30">
                          <Users className="w-14 h-14 text-slate-400" />
                        </div>
                        <p className="text-slate-300 font-medium text-lg">Founder Portrait</p>
                        <p className="text-slate-500 text-sm mt-1">Placeholder</p>
                      </div>
                    </div>
                  )}
                  {/* Decorative corner accents */}
                  <div className="absolute top-4 left-4 w-20 h-20 border-t-3 border-l-3 border-primary/50 rounded-tl-xl" style={{borderWidth: '3px'}} />
                  <div className="absolute bottom-4 right-4 w-20 h-20 border-b-3 border-r-3 border-primary/50 rounded-br-xl" style={{borderWidth: '3px'}} />
                </div>
                {/* Background decorative element */}
                <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full bg-primary/10 rounded-2xl hidden lg:block" />
              </div>
            </div>

            {/* RIGHT: Founder Identity Content */}
            <div className="order-2 lg:order-2">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Founder & Leadership</Badge>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
                {headline}
              </h1>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-3" data-testid="founder-name">
                {name}
              </h2>
              
              <div className="space-y-1 mb-6">
                <p className="text-lg text-slate-600 font-medium">
                  {role}
                </p>
                <p className="text-lg text-slate-600 font-medium">
                  {org}
                </p>
              </div>
              
              {/* Signature Statement */}
              <div className="relative mb-8 pl-6 py-4 border-l-4 border-primary bg-gradient-to-r from-primary/5 to-transparent rounded-r-lg">
                <Sparkles className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-primary bg-white rounded-full p-1" />
                <p className="text-xl sm:text-2xl font-semibold text-slate-800 italic" data-testid="founder-quote">
                  "{quote}"
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={ctaPrimaryLink}>
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all group"
                  >
                    {ctaPrimaryText}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={scrollToContact}
                  className="w-full sm:w-auto border-2 border-slate-300 hover:border-primary hover:text-primary font-semibold px-8 py-6 text-lg rounded-xl transition-all"
                >
                  {ctaSecondaryText}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: IN ACTION - AUTHENTICITY LAYER ========== */}
      <section className="relative py-20 bg-slate-900 overflow-hidden">
        {/* Action Photo or Placeholder */}
        <div className="absolute inset-0">
          {actionImageUrl ? (
            <>
              <img
                src={actionImageUrl}
                alt="In Action"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-slate-900/40" />
            </>
          ) : (
            <>
              <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-900 to-primary/30 flex items-center justify-center">
                {/* Placeholder Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white/20 rounded-full" />
                  <div className="absolute bottom-20 right-20 w-48 h-48 border-2 border-white/20 rounded-full" />
                  <div className="absolute top-1/2 left-1/4 w-24 h-24 border-2 border-white/20 rounded-full" />
                </div>
              </div>
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-slate-900/40" />
            </>
          )}
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Placeholder indicator (only when no image) */}
          {!actionImageUrl && (
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/60 text-sm mb-8 backdrop-blur-sm">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                In-Water Training Placeholder
              </div>
            </div>
          )}
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {actionHeadline}
          </h2>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {actionText}
          </p>
        </div>
      </section>

      {/* ========== SECTION 3: VISION & IMPACT ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* LEFT: Our Vision */}
            <div className="relative">
              <div className="absolute -left-4 top-0 w-1 h-24 bg-gradient-to-b from-primary to-cyan-400 rounded-full hidden lg:block" />
              <Badge className="mb-4 bg-cyan-50 text-cyan-700 border-cyan-200">Our Vision</Badge>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                {visionTitle}
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                {visionText}
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Zero Drowning Vision</p>
                  <p className="text-sm text-slate-500">Working toward water-safe communities</p>
                </div>
              </div>
            </div>

            {/* RIGHT: Our System */}
            <div className="bg-slate-50 rounded-2xl p-8 lg:p-10">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Our System</Badge>
              <h3 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
                {systemTitle}
              </h3>
              <ul className="space-y-4">
                {systemChecklist.map((item, idx) => {
                  const IconComponent = CHECKLIST_ICONS[idx % CHECKLIST_ICONS.length];
                  return (
                    <li key={idx} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 text-primary" />
                      </div>
                      <p className="text-slate-700 pt-2">{item}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 4: AUTHORITY BADGE STRIP ========== */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {authorityBadges.map((badge, idx) => {
              const IconComponent = BADGE_ICONS[idx % BADGE_ICONS.length];
              return (
                <div 
                  key={idx}
                  className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-primary/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CheckCircle className="w-4 h-4 text-emerald-400 mb-1" />
                    <p className="text-white font-medium text-sm">{badge.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== SECTION 5: FINAL CTA ========== */}
      <section ref={contactRef} className="py-20 bg-gradient-to-br from-primary/5 via-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-white text-primary border-primary/20 shadow-sm">Join the Movement</Badge>
          
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            {ctaSectionHeadline}
          </h2>
          
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            {ctaSectionText}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#programs">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-semibold px-10 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all group"
              >
                {ctaEnrollText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/#contact">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-slate-300 hover:border-primary hover:text-primary font-semibold px-10 py-6 text-lg rounded-xl transition-all"
              >
                {ctaPartnerText}
              </Button>
            </Link>
          </div>
          
          {/* Quick Contact Info */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500 mb-4">Or reach out directly:</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-600">
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="founder-phone">
                <Phone className="w-4 h-4" />
                <span>{phone}</span>
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-primary transition-colors" data-testid="founder-email">
                <Mail className="w-4 h-4" />
                <span>{email}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="py-8 bg-slate-900 text-center">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">
            ← Back to Eswatini Seals Swim Academy
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default FounderPage;
